import React, { useEffect, useState } from 'react';
import { generateSitemap } from 'utils/sitemapGenerator';

// Cache configuration
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
let sitemapCache = null;
let cacheTimestamp = null;

/**
 * Clear sitemap cache
 */
export const clearSitemapCache = () => {
  sitemapCache = null;
  cacheTimestamp = null;
  console.log('Sitemap cache cleared');
};

/**
 * Check if cache is valid
 */
const isCacheValid = () => {
  if (!sitemapCache || !cacheTimestamp) return false;
  return (Date.now() - cacheTimestamp) < CACHE_DURATION;
};

/**
 * React component that serves sitemap.xml at /sitemap.xml route
 */
const SitemapXMLRoute = () => {
  const [sitemapData, setSitemapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSitemap = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check cache first
        if (isCacheValid()) {
          console.log('Serving sitemap from cache');
          setSitemapData(sitemapCache);
          setLoading(false);
          return;
        }

        // Generate new sitemap
        console.log('Generating new sitemap...');
        const result = await generateSitemap();
        
        // Update cache
        sitemapCache = result;
        cacheTimestamp = Date.now();
        
        setSitemapData(result);
        setLoading(false);
        
        console.log(`Sitemap generated with ${result.totalUrls} URLs`);
      } catch (err) {
        console.error('Error loading sitemap:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadSitemap();
  }, []);

  // Set proper content type for XML
  useEffect(() => {
    if (sitemapData && !loading) {
      // Set content type header (this won't work in development but will work when deployed)
      document.title = 'Sitemap';
    }
  }, [sitemapData, loading]);

  if (loading) {
    return (
      <div style={{ 
        fontFamily: 'monospace', 
        padding: '20px', 
        backgroundColor: '#f5f5f5',
        minHeight: '100vh'
      }}>
        <h2>Generating Sitemap...</h2>
        <p>Please wait while we generate the sitemap with the latest content.</p>
        <div style={{ 
          border: '1px solid #ddd', 
          padding: '10px', 
          backgroundColor: 'white',
          borderRadius: '4px'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        fontFamily: 'monospace', 
        padding: '20px', 
        backgroundColor: '#f5f5f5',
        minHeight: '100vh'
      }}>
        <h2>Error Generating Sitemap</h2>
        <p style={{ color: 'red' }}>Error: {error}</p>
        <p>Please try refreshing the page or contact support.</p>
      </div>
    );
  }

  const handleDownload = () => {
    if (!sitemapData?.xml) return;
    const blob = new Blob([sitemapData.xml], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sitemap.xml';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Display XML content
  return (
    <div style={{ 
      fontFamily: 'monospace', 
      fontSize: '12px',
      lineHeight: '1.4',
      margin: 0,
      padding: 0,
      backgroundColor: '#ffffff'
    }}>
      {/* Download button - saves as proper .xml file instead of HTML */}
      <div style={{ padding: '10px', borderBottom: '1px solid #eee', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button
          onClick={handleDownload}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--primary-color)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          Download sitemap.xml
        </button>
        <span style={{ color: '#666', fontSize: '13px' }}>
          Use this to save as .xml file (browser Save will save the webpage)
        </span>
      </div>
      {/* XML content displayed as plain text for browser compatibility */}
      <pre style={{ 
        margin: 0, 
        padding: '10px',
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word'
      }}>
        {sitemapData?.xml}
      </pre>
      
      {/* Hidden metadata for debugging (only visible in source) */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ display: 'none' }}>
          {/* Metadata */}
          Generated: {sitemapData?.generatedAt}
          Total URLs: {sitemapData?.totalUrls}
          Static URLs: {sitemapData?.staticUrls}
          Dynamic URLs: {sitemapData?.dynamicUrls}
          Success: {sitemapData?.success ? 'true' : 'false'}
          {sitemapData?.error && `Error: ${sitemapData.error}`}
        </div>
      )}
    </div>
  );
};

export default SitemapXMLRoute;